import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  RefreshCw,
  Bitcoin,
  Banknote,
  Wallet,
  Calendar,
  Sparkles,
  FileDown,
  Loader2,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const fmtBdt = (n) =>
  `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtUsdt = (n) =>
  `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}`;

const PERIODS = [
  { key: "today",        label: "Today",        short: "Today",  accent: "#10B981" },
  { key: "yesterday",    label: "Yesterday",    short: "Yest.",  accent: "#8B5CF6" },
  { key: "last_3_days",  label: "Last 3 Days",  short: "3D",     accent: "#F43F5E" },
  { key: "last_7_days",  label: "Last 7 Days",  short: "7D",     accent: "#F59E0B" },
  { key: "last_15_days", label: "Last 15 Days", short: "15D",    accent: "#14B8A6" },
  { key: "last_30_days", label: "Last 30 Days", short: "30D",    accent: "#3B82F6" },
];

export default function AgentSelfDepositDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agents/self-deposit-dashboard");
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/agents/self-deposit-dashboard.pdf", {
        responseType: "blob",
      });
      const ts = new Date().toISOString().slice(0, 16).replace("T", "-").replace(":", "");
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `agent-self-deposit-dashboard-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const usdtRate = Number(data?.usdt_bdt_rate || 0);

  return (
    <div data-testid="agent-self-deposit-dashboard" className="pb-6">
      <PageHeader
        title="Agent Panel Self-Deposit Dashboard"
        subtitle="Agent panel-এ নিজে কত টাকা ডিপোজিট করেছে — BDT + USDT, period অনুযায়ী।"
        actions={
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
            <button
              onClick={downloadPdf}
              disabled={downloading || loading || !data}
              className="h-9 px-2.5 sm:px-3 inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] sm:text-[13px] rounded-sm font-semibold disabled:opacity-50"
              data-testid="agent-self-dep-download-pdf"
            >
              {downloading ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />}
              <span className="hidden sm:inline">Download PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>
            <button
              onClick={load}
              disabled={loading}
              className="h-9 px-2.5 sm:px-3 inline-flex items-center gap-1.5 sm:gap-2 bg-white border border-gray-200 hover:border-emerald-300 rounded-sm text-[12px] sm:text-[13px] disabled:opacity-50"
              data-testid="agent-self-dep-refresh"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        }
      />

      {data?.generated_at && (
        <div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
          <div className="text-[10.5px] sm:text-[11px] text-gray-500 inline-flex items-center gap-1.5">
            <Sparkles size={11} className="text-emerald-500" />
            <span className="font-mono text-gray-700">
              {new Date(data.generated_at).toLocaleString("en-GB", { timeZone: "Asia/Dhaka" })}
            </span>
            <span className="text-gray-400 hidden sm:inline">· {data.timezone}</span>
          </div>
          {usdtRate > 0 && (
            <span
              className="inline-flex items-center gap-1 text-[10px] sm:text-[10.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-200"
              data-testid="usdt-rate-locked-badge"
              title={`Locked conversion rate currently set in USDT Deposit config: 1 USDT = ৳${usdtRate.toFixed(2)} BDT`}
            >
              <Bitcoin size={10} />
              Rate locked: 1 USDT = ৳{usdtRate.toFixed(2)}
            </span>
          )}
        </div>
      )}

      {/* ====== BDT SECTION ====== */}
      <SectionHeader
        Icon={Banknote}
        title="BDT Deposit — Successful only"
        accent="#10B981"
        bgClass="bg-emerald-50/50"
        ringClass="border-emerald-200"
        textClass="text-emerald-700"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-5 sm:mb-6">
        {PERIODS.map(({ key, label, accent }) => (
          <CurrencyCard
            key={`bdt-${key}`}
            label={label}
            accent={accent}
            currency="BDT"
            mainValue={fmtBdt(data?.[key]?.bdt_amount)}
            count={data?.[key]?.bdt_count || 0}
            loading={loading}
            testid={`bdt-${key}`}
          />
        ))}
      </div>

      {/* ====== USDT SECTION ====== */}
      <SectionHeader
        Icon={Bitcoin}
        title="USDT Deposit — Successful only"
        accent="#F59E0B"
        bgClass="bg-amber-50/50"
        ringClass="border-amber-200"
        textClass="text-amber-700"
        rightBadge={usdtRate > 0 ? `1 USDT = ৳${usdtRate.toFixed(2)}` : null}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-5 sm:mb-6">
        {PERIODS.map(({ key, label, accent }) => (
          <CurrencyCard
            key={`usdt-${key}`}
            label={label}
            accent={accent}
            currency="USDT"
            mainValue={`${fmtUsdt(data?.[key]?.usdt_amount)} USDT`}
            sub={`≈ ${fmtBdt(data?.[key]?.usdt_bdt_value)} BDT`}
            count={data?.[key]?.usdt_count || 0}
            loading={loading}
            testid={`usdt-${key}`}
          />
        ))}
      </div>

      {/* ====== GRAND SUMMARY ====== */}
      {data && (
        <>
          <SectionHeader
            Icon={Wallet}
            title="Grand Summary — Last 30 Days"
            accent="#8B5CF6"
            bgClass="bg-violet-50/50"
            ringClass="border-violet-200"
            textClass="text-violet-700"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <GrandStat
              icon={Banknote}
              label="BDT (30d)"
              value={fmtBdt(data.last_30_days?.bdt_amount)}
              sub={`${data.last_30_days?.bdt_count || 0} successful deposits`}
              tone="emerald"
            />
            <GrandStat
              icon={Bitcoin}
              label="USDT (30d)"
              value={`${fmtUsdt(data.last_30_days?.usdt_amount)} USDT`}
              sub={`${data.last_30_days?.usdt_count || 0} deposits · ≈ ${fmtBdt(data.last_30_days?.usdt_bdt_value)} BDT`}
              tone="amber"
            />
            <GrandStat
              icon={Wallet}
              label="Combined (BDT)"
              value={fmtBdt(data.last_30_days?.total_bdt)}
              sub={`${data.last_30_days?.total_count || 0} total deposits`}
              tone="violet"
            />
          </div>
        </>
      )}
    </div>
  );
}

function SectionHeader({ Icon, title, accent, bgClass, ringClass, textClass, rightBadge }) {
  return (
    <div
      className={`rounded-sm border ${ringClass} ${bgClass} px-3 sm:px-4 py-2 sm:py-2.5 mb-3 flex items-center gap-2`}
      style={{ borderLeftWidth: 4, borderLeftColor: accent }}
    >
      <Icon size={15} style={{ color: accent }} className="shrink-0" />
      <h3 className={`text-[11.5px] sm:text-[13px] font-extrabold uppercase tracking-wider ${textClass} flex-1 min-w-0 truncate`}>
        {title}
      </h3>
      {rightBadge && (
        <span
          className="hidden md:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border"
          style={{ background: `${accent}1a`, color: accent, borderColor: `${accent}40` }}
        >
          <TrendingUp size={9} />
          {rightBadge}
        </span>
      )}
    </div>
  );
}

function CurrencyCard({ label, accent, currency, mainValue, sub, count, loading, testid }) {
  const empty = !count || count === 0;
  return (
    <div
      className="relative rounded-sm border border-gray-200 bg-white p-3 sm:p-4 overflow-hidden"
      data-testid={`agent-self-dep-card-${testid}`}
      style={{ boxShadow: "0 1px 0 rgba(0,0,0,0.02)" }}
    >
      <div
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ background: accent }}
      />
      <div className="pl-2.5 sm:pl-3">
        <div className="flex items-center justify-between mb-2.5 sm:mb-3 gap-2">
          <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-[1.5px] font-bold text-gray-500 min-w-0 truncate">
            <Calendar size={11} style={{ color: accent }} className="shrink-0" />
            <span className="truncate">{label}</span>
          </div>
          <span
            className="text-[9.5px] sm:text-[10px] font-bold uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
            style={{ background: `${accent}1a`, color: accent }}
          >
            {count} {currency}
          </span>
        </div>

        {loading ? (
          <div className="h-14 sm:h-16 flex items-center justify-center text-xs text-gray-400">
            Loading…
          </div>
        ) : empty ? (
          <div className="py-1.5 sm:py-2">
            <div className="text-[18px] sm:text-[20px] font-black tabular-nums text-gray-300 truncate" title={mainValue}>
              {mainValue}
            </div>
            <div className="text-[10px] sm:text-[10.5px] text-gray-400 mt-0.5">No {currency} deposits</div>
          </div>
        ) : (
          <>
            <div
              className="text-[20px] sm:text-[22px] font-black tabular-nums leading-tight truncate"
              style={{ color: accent }}
              title={mainValue}
            >
              {mainValue}
            </div>
            {sub && (
              <div className="text-[10.5px] sm:text-[11px] text-gray-500 mt-0.5 font-semibold truncate" title={sub}>{sub}</div>
            )}
            <div className="text-[10px] sm:text-[10.5px] text-gray-500 mt-1 sm:mt-1.5 font-medium">
              {count} successful {currency} deposit{count === 1 ? "" : "s"}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function GrandStat({ icon: Icon, label, value, sub, tone }) {
  const tones = {
    emerald: { ring: "border-emerald-200", bg: "bg-emerald-50/50", icon: "bg-emerald-100 text-emerald-700", text: "text-emerald-700" },
    amber:   { ring: "border-amber-200",   bg: "bg-amber-50/50",   icon: "bg-amber-100 text-amber-700",     text: "text-amber-700" },
    violet:  { ring: "border-violet-200",  bg: "bg-violet-50/50",  icon: "bg-violet-100 text-violet-700",   text: "text-violet-700" },
  };
  const t = tones[tone] || tones.emerald;
  return (
    <div className={`rounded-sm border ${t.ring} ${t.bg} p-3 sm:p-4`}>
      <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
        <div className={`h-7 w-7 sm:h-8 sm:w-8 rounded-sm grid place-items-center ${t.icon}`}>
          <Icon size={13} />
        </div>
        <div className="text-[10px] sm:text-[10.5px] uppercase tracking-wider font-bold text-gray-600 truncate">
          {label}
        </div>
      </div>
      <div className={`text-lg sm:text-xl font-black tabular-nums ${t.text} truncate`} title={value}>{value}</div>
      <div className="text-[10.5px] sm:text-[11px] text-gray-500 mt-0.5 truncate" title={sub}>{sub}</div>
    </div>
  );
}
